import {
  analyzeChain,
  enumerateAssignments,
  tableDistribution,
  tableOutcomes,
} from '../story/chainTree';
import {
  EFFECT_BLOCKS,
  EFFECT_CAPS,
  EFFECT_PER_PATH,
  EFFECT_RUNS,
  EFFECT_TYPES,
  type EffectType,
} from '../story/effects';
import {
  MAX_DEPTH,
  OUTCOME_TONES,
  type BeatChainDef,
  type CellDef,
  type EffectDef,
  type HiddenVarDef,
  type OutcomeTableCase,
  type StoryRulesDef,
} from '../story/types';
import { STAT_IDS } from '../hb/types';
import { storyRulesDefaults } from '../storyDefaults';
import { withDefaults } from '../teamsDefaults';
import { isBlank, num, type Err, type Ids } from './common';
import { checkCond, readsRaidFact, tryInt, type VarType } from './conds';
import { idSet, sectionEntries } from './sections';
import type { Bundle } from './types';

/**
 * The beat engine's sections (`beatChains`, `allies`, `story`): a port of shared-cs `ContentValidator.Beats.cs` (S1-ENG), with
 * `StoryConditions.Validate` (conds.ts), `EffectRegistry.Validate`, `TradeRules.Validate` and `BeatAnalysis.GoodPaths`. The same checks in
 * the same order with the same messages; each issue also carries the dotted path of the field it's about so the beat editor can show it
 * inline. `ContentValidator.BeatWarnings` (a certain tell, a windfall share over `story.tone.maxWindfallShare`, a dominant root option)
 * become client warnings; the server simulates 400 tickets for the share where this uses the expected values.
 */

/** shared-cs `BeatRules`. */
const MAX_ROLL_SHEET_CEILING = 200;
const MIN_TIMER_S = 3;
const MAX_TIMER_S = 20;
const HIDDEN_VAR_TYPES = ['bool', 'int', 'enum'];

/** The `story` block with its C# defaults filled in (nested blocks too), or null when it's explicitly null. */
export function storyRules(b: Bundle): Required<StoryRulesDef> | null {
  if (b.story === null) return null;
  const d = storyRulesDefaults();
  const s = withDefaults(d, (b.story ?? d) as Required<StoryRulesDef>);
  return {
    ...s,
    tone: { ...d.tone, ...s.tone, targetMix: { ...d.tone.targetMix, ...s.tone?.targetMix } },
    windfall: { ...d.windfall, ...s.windfall },
    trade: { ...d.trade, ...s.trade },
    ally: { ...d.ally, ...s.ally },
  };
}

/** `BeatRules.Depth` and `BeatRules.RollSheet`. */
export const ruleDepth = (r: StoryRulesDef | null | undefined) =>
  Math.max(1, Math.min(MAX_DEPTH, r?.maxDepth ?? MAX_DEPTH));
export const ruleRollSheet = (r: StoryRulesDef | null | undefined) =>
  Math.max(1, Math.min(MAX_ROLL_SHEET_CEILING, r?.maxRollSheetEntries ?? 120));

/** The `story` ranges in the server's order: [path, min, max]. */
export const STORY_RANGES: [string, number, number][] = [
  ['maxDepth', 1, MAX_DEPTH],
  ['defaultTimerS', MIN_TIMER_S, MAX_TIMER_S],
  ['relaxedTimerMult', 1, 3],
  ['cellMinDwellMs', 0, 1500],
  ['maxCellsPerBeat', 1, 6],
  ['maxChainsPerTicket', 0, 5],
  ['maxRollSheetEntries', 1, MAX_ROLL_SHEET_CEILING],
  ['maxStoryBytes', 1024, 65536],
  ['carryTurnHours', 0.25, 72],
  ['factSlackMs', 0, 10000],
  ['echoChance', 0, 1],
  ['absentBadOutcomeWeight', 0.1, 10],
];
export const TONE_RANGES: [string, number, number][] = [
  ['tone.targetMix.disaster', 0, 60],
  ['tone.targetMix.bad', 0, 60],
  ['tone.targetMix.mixed', 0, 60],
  ['tone.targetMix.good', 0, 60],
  ['tone.targetMix.windfall', 0, 60],
];
export const LATER_RANGES: [string, number, number][] = [
  ['tone.maxWindfallShare', 0, 100],
  ['windfall.valuePerRunDay', 0, 1000],
  ['windfall.stashValueMax', 0, 400],
  ['windfall.tradeBonusValueMax', 0, 200],
  ['windfall.homeValueMax', 0, 200],
  ['trade.shortPct', 30, 70],
  ['ally.maxActive', 1, 4],
  ['ally.deliveries', 0, 5],
  ['ally.everyHours', 6, 72],
  ['ally.jitterHours', 0, 12],
  ['ally.deliveryValueMax', 10, 100],
  ['ally.echoAfterRaids', 1, 10],
];

const get = (o: unknown, path: string): number =>
  num(
    path
      .split('.')
      .reduce<unknown>((x, k) => (x as Record<string, unknown> | undefined)?.[k], o) as
      number | undefined,
  );

function validateStory(b: Bundle, err: Err) {
  if (b.story === null) err('story', undefined, '', "Bundle is missing 'story'.");
  const s = storyRules(b) ?? storyRulesDefaults();
  const range = ([path, min, max]: [string, number, number]) => {
    const v = get(s, path);
    if (Number.isNaN(v) || v < min || v > max)
      err('story', undefined, path, `story.${path} must be ${min}..${max}.`);
  };
  STORY_RANGES.forEach(range);
  if (s.validationMode !== 'report' && s.validationMode !== 'reject')
    err('story', undefined, 'validationMode', 'story.validationMode must be report or reject.');
  TONE_RANGES.forEach(range);
  const m = s.tone.targetMix ?? {};
  if (num(m.disaster) + num(m.bad) + num(m.mixed) + num(m.good) + num(m.windfall) !== 100)
    err('story', undefined, 'tone.targetMix', 'story.tone.targetMix must sum to 100.');
  LATER_RANGES.forEach(range);
}

export type BeatRefs = {
  items: Set<string>;
  tables: Set<string>;
  zombies: Set<string>;
  roles: Set<string>;
};

/** A problem inside one chain: the field path and the server's message without its final period. */
type Sub = [path: string, message: string];

export function validateBeats(b: Bundle, refs: BeatRefs, err: Err, warn: Err, ids: Ids) {
  validateStory(b, err);
  const rules = storyRules(b) ?? storyRulesDefaults();
  const allies = sectionEntries('allies', b.allies, err);
  const chains = sectionEntries('beatChains', b.beatChains, err);
  ids('beatChains', b.beatChains ?? [], 'beat chain');
  ids('allies', b.allies ?? [], 'ally');
  const chainIds = idSet(b.beatChains);

  for (const [a, i] of allies) {
    const where = `Ally '${a.id ?? ''}'`;
    const fail = (path: string, m: string) => err('allies', i, path, m);
    if (isBlank(a.name)) fail('name', `${where} needs a name.`);
    if (isBlank(a.look)) fail('look', `${where} needs a look (from_role or a look id).`);
    const d = { count: 3, everyHours: 24, table: '', valueMax: 40, ...a.deliveries };
    if (num(d.count) < 0 || num(d.count) > 5)
      fail('deliveries.count', `${where} deliveries count must be 0..5.`);
    if (num(d.count) > 0) {
      if (num(d.everyHours) < 6 || num(d.everyHours) > 72)
        fail('deliveries.everyHours', `${where} deliveries everyHours must be 6..72.`);
      if (!refs.tables.has(d.table ?? ''))
        fail('deliveries.table', `${where} delivery table '${d.table ?? ''}' doesn't exist.`);
      if (num(d.valueMax) < 10 || num(d.valueMax) > 100)
        fail('deliveries.valueMax', `${where} delivery valueMax must be 10..100.`);
    }
    (a.echoes ?? []).forEach((e, j) => {
      if (!chainIds.has(e)) fail(`echoes.${j}`, `${where} echo chain '${e}' doesn't exist.`);
    });
  }

  for (const [c, i] of chains) {
    const sub = beatChainProblems(c, b, rules, refs);
    for (const [path, m] of sub) err('beatChains', i, path, `${m}.`);
    if (sub.length === 0 && c.status !== 'retired')
      for (const [path, m] of beatWarnings(c, rules)) warn('beatChains', i, path, m);
  }
}

/** `ContentValidator.BeatChain` (+ `Structure` when everything else is clean): [path, message] pairs. */
export function beatChainProblems(
  c: BeatChainDef,
  b: Bundle,
  rules: StoryRulesDef,
  refs: BeatRefs,
): Sub[] {
  const chainWhere = `Beat chain '${c.id ?? ''}'`;
  const sub: Sub[] = [];
  const add = (path: string, m: string) => sub.push([path, m]);
  if (!['live', 'dev_only', 'retired'].includes(c.status ?? 'live'))
    add('status', `${chainWhere}: status must be live, dev_only or retired`);
  if (isBlank(c.title)) add('title', `${chainWhere}: needs a title`);

  // Cast.
  const roles = new Set<string>();
  const npcIds = idSet(b.npcs);
  const allyIds = idSet(b.allies);
  (c.cast ?? []).forEach((r, j) => {
    const at = (f: string) => `cast.${j}.${f}`;
    if (r == null || !r.role) {
      add(at('role'), `${chainWhere}: a cast role has no name`);
      return;
    }
    const where = `${chainWhere} role '${r.role}'`;
    if (r.role === 'player' || r.role === 'leader' || r.role === 'team')
      add(at('role'), `${where}: player, leader and team are built-in roles`);
    if (roles.has(r.role)) add(at('role'), `${where}: duplicate role`);
    roles.add(r.role);
    switch (r.bind ?? 'squad_member') {
      case 'squad_member':
        if (!(r.exclude ?? []).includes('player'))
          add(at('exclude'), `${where}: a squad_member role must exclude the player`);
        break;
      case 'npc':
        if (!r.archetype && !r.npcId)
          add(at('archetype'), `${where}: an npc role needs an archetype or an npcId`);
        if (r.npcId && !npcIds.has(r.npcId))
          add(at('npcId'), `${where}: npc '${r.npcId}' doesn't exist`);
        break;
      case 'ledger':
        if (!r.ledgerChain || !r.ledgerRole)
          add(at('ledgerChain'), `${where}: a ledger role needs ledgerChain and ledgerRole`);
        break;
      case 'ally':
        if (!allyIds.has(r.allyId ?? ''))
          add(at('allyId'), `${where}: ally '${r.allyId ?? ''}' isn't an allies[] def`);
        break;
      default:
        add(at('bind'), `${where}: bind must be squad_member, npc, ledger or ally`);
    }
  });

  // Hidden vars and tells.
  const varTypes = new Map<string, { type: string; values: string[] }>();
  const varType: VarType = (id) => varTypes.get(id);
  (c.hidden ?? []).forEach((v, j) => {
    const at = (f: string) => `hidden.${j}${f ? `.${f}` : ''}`;
    if (v == null || !v.id) {
      add(at('id'), `${chainWhere}: a hidden variable has no id`);
      return;
    }
    const where = `${chainWhere} var '${v.id}'`;
    if (varTypes.has(v.id)) add(at('id'), `${where}: duplicate id`);
    if (!HIDDEN_VAR_TYPES.includes(v.type ?? 'bool'))
      add(at('type'), `${where}: type must be bool, int or enum`);
    if (v.type === 'enum' && (v.values ?? []).length < 2)
      add(at('values'), `${where}: an enum needs at least 2 values`);
    const cases = v.cases ?? [];
    if (cases.length === 0 || !cases[cases.length - 1]?.else)
      add(at('cases'), `${where}: the last case must be else`);
    cases.forEach((cs, n) => {
      if (cs == null) return;
      if (cs.else && n < cases.length - 1)
        add(at(`cases.${n}`), `${where}: only the last case may be else`);
      if (!cs.else && cs.if == null)
        add(at(`cases.${n}`), `${where}: case ${n} needs an if or else`);
      checkCond(
        cs.if,
        `${where} case ${n}`,
        varType,
        roles,
        false,
        false,
        add,
        at(`cases.${n}.if`),
      );
      let total = 0;
      (cs.pick ?? []).forEach((p, q) => {
        if (p == null) return;
        if (num(p.w) < 0) add(at(`cases.${n}.pick.${q}.w`), `${where}: a weight can't be negative`);
        total += Math.max(0, num(p.w));
        if (!valueFits(v, p.value))
          add(
            at(`cases.${n}.pick.${q}.value`),
            `${where}: '${p.value ?? ''}' isn't a ${v.type ?? 'bool'} value`,
          );
      });
      if (total <= 0) add(at(`cases.${n}.pick`), `${where}: case ${n} needs a positive weight`);
    });
    varTypes.set(v.id, {
      type: v.type === 'enum' ? 'enum' : (v.type ?? 'bool'),
      values: v.values ?? [],
    });
  });

  (c.tells ?? []).forEach((t, j) => {
    const at = (f: string) => `tells.${j}.${f}`;
    if (t == null || !t.id) {
      add(at('id'), `${chainWhere}: a tell has no id`);
      return;
    }
    const where = `${chainWhere} tell '${t.id}'`;
    if (varTypes.has(t.id)) add(at('id'), `${where}: duplicate id`);
    const parent = (c.hidden ?? []).find((h) => h != null && h.id === t.of);
    if (!parent) add(at('of'), `${where}: 'of' names no hidden variable`);
    for (const [k, p] of Object.entries(t.p ?? {})) {
      if (Number.isNaN(p) || p < 0 || p > 1) add(at('p'), `${where}: P(${k}) must be 0..1`);
      if (parent && !valueFits(parent, k))
        add(at('p'), `${where}: '${k}' isn't a value of '${t.of ?? ''}'`);
    }
    varTypes.set(t.id, { type: 'bool', values: [] });
  });

  checkCond(c.eligible, `${chainWhere} eligible`, varType, roles, false, false, add, 'eligible');
  for (const [k, v] of Object.entries(c.strings ?? {}))
    address(v, `${chainWhere} string '${k}'`, `strings.${k}`, add);

  // Beats.
  const beats = c.beats ?? {};
  const tables = c.tables ?? {};
  const outcomes = c.outcomes ?? {};
  if (!c.root || !(c.root in beats))
    add('root', `${chainWhere}: root '${c.root ?? ''}' isn't a beat`);
  const maxCells = Math.max(1, num(rules.maxCellsPerBeat));
  for (const [id, beat] of Object.entries(beats)) {
    const where = `${chainWhere} beat '${id}'`;
    const base = `beats.${id}`;
    if (beat == null) {
      add(base, `${where}: empty`);
      continue;
    }
    cells(beat.cells, where, `${base}.cells`, maxCells, varType, roles, add);
    if (beat.choice == null) {
      if (beat.outcome && !(beat.outcome in outcomes))
        add(`${base}.outcome`, `${where}: outcome '${beat.outcome}' doesn't exist`);
      continue;
    }
    if (beat.outcome)
      add(`${base}.outcome`, `${where}: a beat with a choice can't also name an outcome`);
    const ch = beat.choice;
    const options = ch.options ?? [];
    // Day Zero (contract §5.3): a choice with showIf options may hold up to 8 (the server checks every deal leaves 2 or 3 visible).
    const hand = options.some((o) => (o as { showIf?: unknown } | null)?.showIf != null);
    // Card #532: one option is a page that asks and waits (Day Zero's I CAN FIGHT.).
    if (!hand && (options.length < 1 || options.length > 3))
      add(`${base}.choice.options`, `${where}: a choice has 1 to 3 options`);
    if (hand && (options.length < 2 || options.length > 8))
      add(`${base}.choice.options`, `${where}: a choice with showIf options has 2..8 options`);
    const optionIds = new Set<string>();
    options.forEach((o, n) => {
      const at = (f: string) => `${base}.choice.options.${n}.${f}`;
      if (o == null || !o.id) {
        add(at('id'), `${where}: an option has no id`);
        return;
      }
      if (optionIds.has(o.id)) add(at('id'), `${where}: duplicate option '${o.id}'`);
      optionIds.add(o.id);
      const ow = `${where} option '${o.id}'`;
      if (isBlank(o.label)) add(at('label'), `${ow}: needs a label`);
      if (!((o.table ?? '') in tables))
        add(at('table'), `${ow}: table '${o.table ?? ''}' doesn't exist`);
      checkCond(o.requires, `${ow} requires`, varType, roles, true, true, add, at('requires'));
      if (o.requires != null && isBlank(o.requiresLabel))
        add(at('requiresLabel'), `${ow}: requires needs a requiresLabel`);
      address(o.label, ow, at('label'), add);
    });
    if (ch.timerS != null) {
      if (ch.timerS < MIN_TIMER_S || ch.timerS > MAX_TIMER_S)
        add(`${base}.choice.timerS`, `${where}: timerS must be 3..20 or null`);
      const timeout = options.find((o) => o != null && o.id === ch.timeout);
      if (!timeout) add(`${base}.choice.timeout`, `${where}: timeout must be one of the options`);
      else if (timeout.requires != null)
        add(`${base}.choice.timeout`, `${where}: the timeout option can't have requires`);
    } else if (ch.timeout) {
      add(`${base}.choice.timeout`, `${where}: a timeout needs a timer`);
    }
    address(ch.prompt, `${where} prompt`, `${base}.choice.prompt`, add);
  }

  // Tables.
  for (const [id, raw] of Object.entries(tables)) {
    const where = `${chainWhere} table '${id}'`;
    const base = `tables.${id}`;
    const cases = raw ?? [];
    if (cases.length === 0 || !cases[cases.length - 1]?.else)
      add(base, `${where}: the last case must be else`);
    cases.forEach((cs, n) => {
      if (cs == null) return;
      if (cs.else && n < cases.length - 1)
        add(`${base}.${n}`, `${where}: only the last case may be else`);
      if (!cs.else && cs.if == null) add(`${base}.${n}`, `${where}: case ${n} needs an if or else`);
      checkCond(cs.if, `${where} case ${n}`, varType, roles, true, true, add, `${base}.${n}.if`);
      let total = 0;
      (cs.pick ?? []).forEach((p, q) => {
        if (p == null) return;
        if (num(p.w) < 0) add(`${base}.${n}.pick.${q}.w`, `${where}: a weight can't be negative`);
        total += Math.max(0, num(p.w));
        if (!((p.outcome ?? '') in outcomes))
          add(
            `${base}.${n}.pick.${q}.outcome`,
            `${where}: outcome '${p.outcome ?? ''}' doesn't exist`,
          );
      });
      if (total <= 0) add(`${base}.${n}.pick`, `${where}: case ${n} needs a positive weight`);
      statWeightProblems(
        cs as OutcomeTableCase,
        `${where} case ${n}`,
        `${base}.${n}.statWeight`,
        roles,
        add,
      );
    });
    // Card 19: a stat-weighted pick is made at issue, so the table can't leave it to a raid fact.
    if (
      cases.some((x) => (x as OutcomeTableCase | null)?.statWeight != null) &&
      cases.some((x) => x != null && readsRaidFact(x.if))
    )
      add(
        base,
        `${where}: a statWeight table can't read raid facts (its pick is made at ticket issue)`,
      );
  }

  // Outcomes.
  let anyGood = false;
  const outcomeIds = Object.keys(outcomes);
  for (const [id, o] of Object.entries(outcomes)) {
    const where = `${chainWhere} outcome '${id}'`;
    const base = `outcomes.${id}`;
    if (o == null) {
      add(base, `${where}: empty`);
      continue;
    }
    const tone = o.tone ?? 'mixed';
    if (!(OUTCOME_TONES as readonly string[]).includes(tone))
      add(`${base}.tone`, `${where}: tone must be disaster, bad, mixed, good or windfall`);
    if (tone === 'good' || tone === 'windfall') anyGood = true;
    cells(o.cells, where, `${base}.cells`, maxCells, varType, roles, add);
    if (o.next && !(o.next in beats))
      add(`${base}.next`, `${where}: next '${o.next}' isn't a beat`);
    const effects = o.effects ?? [];
    if (effects.length > EFFECT_CAPS.maxPerOutcome)
      add(`${base}.effects`, `${where}: more than ${EFFECT_CAPS.maxPerOutcome} effects`);
    let flags = 0;
    const conditionRoles = new Set<string>();
    effects.forEach((e, n) => {
      validateEffect(e, `${where} effect ${n}`, `${base}.effects.${n}`, roles, b, refs, false, add);
      if (e?.type === 'flag') flags++;
      if (e?.type === 'condition' && e.condition != null) {
        const role = e.condition.role ?? '';
        if (conditionRoles.has(role))
          add(`${base}.effects.${n}`, `${where}: one condition per role`);
        conditionRoles.add(role);
      }
    });
    if (flags > EFFECT_CAPS.flagsPerOutcome)
      add(`${base}.effects`, `${where}: more than ${EFFECT_CAPS.flagsPerOutcome} flags`);
    (o.reveals ?? []).forEach((r, n) => {
      if (!varTypes.has(r))
        add(`${base}.reveals.${n}`, `${where}: reveals '${r}', which isn't a hidden variable`);
    });
  }
  // Day Zero: a tutorial chain (tag dz) or a chain with no choice tells a fixed story.
  const anyChoice = Object.values(c.beats ?? {}).some((x) => x?.choice != null);
  const tutorialChain = (c.tags ?? []).includes('dz');
  if (!anyGood && outcomeIds.length > 0 && anyChoice && !tutorialChain)
    add('outcomes', `${chainWhere}: needs at least one good or windfall outcome`);

  if (sub.length === 0) structure(c, rules, chainWhere, add);
  return sub;
}

/** `ContentValidator.Structure`: depth (beats on a path), cycles, reachability, per-path effect caps, the roll sheet, the tone rule. */
function structure(
  c: BeatChainDef,
  rules: StoryRulesDef,
  chainWhere: string,
  add: (p: string, m: string) => void,
) {
  const maxDepth = ruleDepth(rules);
  const beats = c.beats ?? {};
  const reached = new Set<string>();
  const perPath = new Map<string, number>();
  let cycle = false;
  let tooDeep = false;
  const capHits = new Set<string>();

  const outsOf = (beatId: string): string[] => {
    const beat = beats[beatId];
    if (!beat) return [];
    if (beat.choice == null) return beat.outcome ? [beat.outcome] : [];
    return (beat.choice.options ?? []).flatMap((o) =>
      o ? tableOutcomes(c.tables?.[o.table ?? '']) : [],
    );
  };

  const visit = (beatId: string, depth: number, stack: string[]) => {
    if (cycle) return;
    if (stack.includes(beatId)) {
      cycle = true;
      return;
    }
    if (depth > maxDepth) {
      tooDeep = true;
      return;
    }
    reached.add(beatId);
    if (!beats[beatId]) return;
    stack.push(beatId);
    for (const outcomeId of outsOf(beatId)) {
      const o = c.outcomes?.[outcomeId];
      if (!o) continue;
      const added: string[] = [];
      for (const e of flatten(o.effects)) {
        const cap = EFFECT_PER_PATH[e.type as EffectType] ?? 0;
        if (cap <= 0) continue;
        const n = perPath.get(e.type!) ?? 0;
        perPath.set(e.type!, n + 1);
        added.push(e.type!);
        if (n + 1 > cap) capHits.add(`${e.type} (at most ${cap} per path)`);
      }
      if (o.next) visit(o.next, depth + 1, stack);
      for (const t of added) perPath.set(t, (perPath.get(t) ?? 1) - 1);
    }
    stack.pop();
  };
  visit(c.root ?? '', 1, []);
  if (cycle) {
    add('beats', `${chainWhere}: has a cycle`);
    return;
  }
  if (tooDeep) add('beats', `${chainWhere}: a path holds more than ${maxDepth} choices`);
  for (const id of Object.keys(beats))
    if (!reached.has(id)) add(`beats.${id}`, `${chainWhere}: beat '${id}' is unreachable`);
  for (const hit of [...capHits].sort()) add('outcomes', `${chainWhere}: a path fires ${hit}`);
  if (tooDeep) return;

  let entries = 0;
  const count = (beatId: string, depth: number) => {
    const beat = beats[beatId];
    if (depth > maxDepth || !beat) return;
    const options: (string | null)[] =
      beat.choice == null
        ? beat.outcome
          ? [null]
          : []
        : (beat.choice.options ?? []).map((o) => o?.table ?? '');
    for (const table of options) {
      entries++;
      if (entries > MAX_ROLL_SHEET_CEILING * 4) return;
      const outs = table === null ? [beat.outcome ?? ''] : tableOutcomes(c.tables?.[table]);
      for (const outcomeId of outs) {
        const next = c.outcomes?.[outcomeId]?.next;
        if (next) count(next, depth + 1);
      }
    }
  };
  count(c.root ?? '', 1);
  const limit = ruleRollSheet(rules);
  if (entries > limit)
    add('beats', `${chainWhere}: the roll sheet has ${entries} entries (at most ${limit})`);

  const good = goodPaths(c);
  for (const [id, beat] of Object.entries(beats))
    (beat?.choice?.options ?? []).forEach((o, n) => {
      if (o == null) return;
      if (!good.get(`${id}:${o.id}`) && isBlank(o.toneNote))
        add(
          `beats.${id}.choice.options.${n}.toneNote`,
          `${chainWhere} beat '${id}' option '${o.id}': has no good or windfall path; add a toneNote saying why`,
        );
    });
}

/** `BeatAnalysis.GoodPaths`: beat:option → any good or windfall outcome under it (every roll, every deeper beat). */
export function goodPaths(chain: BeatChainDef): Map<string, boolean> {
  const memo = new Map<string, boolean>();
  const outcomeGood = (id: string, visiting: Set<string>): boolean => {
    const o = chain.outcomes?.[id];
    if (!o) return false;
    if (o.tone === 'good' || o.tone === 'windfall') return true;
    return !!o.next && beatGood(o.next, visiting);
  };
  const optionGood = (table: string, visiting: Set<string>) =>
    tableOutcomes(chain.tables?.[table]).some((x) => outcomeGood(x, visiting));
  const beatGood = (id: string, visiting: Set<string>): boolean => {
    const known = memo.get(id);
    if (known !== undefined) return known;
    if (visiting.has(id)) return false;
    visiting.add(id);
    const b = chain.beats?.[id];
    let good = false;
    if (b) {
      if (b.choice == null) good = !!b.outcome && outcomeGood(b.outcome, visiting);
      else
        good = (b.choice.options ?? []).some(
          (o) => o != null && optionGood(o.table ?? '', visiting),
        );
    }
    visiting.delete(id);
    memo.set(id, good);
    return good;
  };
  const result = new Map<string, boolean>();
  for (const [id, b] of Object.entries(chain.beats ?? {}))
    for (const o of b?.choice?.options ?? [])
      if (o) result.set(`${id}:${o.id}`, optionGood(o.table ?? '', new Set()));
  return result;
}

function cells(
  list: CellDef[] | null | undefined,
  where: string,
  base: string,
  maxCells: number,
  varType: VarType,
  roles: Set<string>,
  add: (p: string, m: string) => void,
) {
  const cs = list ?? [];
  // The decision cell (the choice's own cell) doesn't count toward maxCellsPerBeat (mirrors shared-cs).
  const counted = cs.filter((c) => c != null && !c.panel?.decision).length;
  if (counted > maxCells) add(base, `${where}: more than ${maxCells} cells`);
  cs.forEach((cell, n) => {
    if (cell == null) {
      add(`${base}.${n}`, `${where}: an empty cell`);
      return;
    }
    checkCond(
      cell.showIf,
      `${where} showIf`,
      varType,
      roles,
      true,
      true,
      add,
      `${base}.${n}.showIf`,
    );
    address(cell.panel?.caption?.text, `${where} caption`, `${base}.${n}.panel.caption.text`, add);
    (cell.panel?.dialogue ?? []).forEach((l, k) =>
      address(l?.text, `${where} dialogue`, `${base}.${n}.panel.dialogue.${k}.text`, add),
    );
  });
}

/** `ContentValidator.Address`: cell text never names a real address (no `{…address…}` or `{…street…}` tokens). */
function address(
  text: string | null | undefined,
  where: string,
  path: string,
  add: (p: string, m: string) => void,
) {
  if (!text) return;
  let open = text.indexOf('{');
  while (open >= 0) {
    const close = text.indexOf('}', open);
    if (close < 0) break;
    const token = text.slice(open + 1, close);
    if (/address|street/i.test(token))
      add(path, `${where}: text can't use the address token {${token}}`);
    open = text.indexOf('{', close);
  }
}

function valueFits(v: HiddenVarDef, value: string | null | undefined): boolean {
  switch (v.type ?? 'bool') {
    case 'bool':
      return value === 'true' || value === 'false';
    case 'int':
      return tryInt(value) && Number(value) >= 0 && Number(value) <= 99;
    case 'enum':
      return value != null && (v.values ?? []).includes(value);
    default:
      return false;
  }
}

/** `EffectRegistry.Flatten`: the effects and every delay's `then`. */
export function flatten(effects: (EffectDef | null)[] | null | undefined): EffectDef[] {
  const out: EffectDef[] = [];
  for (const e of effects ?? []) {
    if (e == null) continue;
    out.push(e);
    if (e.type === 'delay' && e.delay)
      for (const inner of e.delay.then ?? []) if (inner) out.push(inner);
  }
  return out;
}

/** `EffectRegistry.Validate` (and `TradeRules.Validate`). Paths point at the block's field. */
function validateEffect(
  e: EffectDef | null,
  where: string,
  base: string,
  roles: Set<string>,
  b: Bundle,
  refs: BeatRefs,
  inDelay: boolean,
  add: (p: string, m: string) => void,
) {
  if (e == null) {
    add(base, `${where}: empty effect`);
    return;
  }
  const type = (e.type ?? '') as EffectType;
  if (!(EFFECT_TYPES as readonly string[]).includes(type)) {
    add(`${base}.type`, `${where}: unknown effect type '${e.type ?? ''}'`);
    return;
  }
  const rec = e as Record<string, unknown>;
  const blockKey = EFFECT_BLOCKS[type];
  const blocks = Object.values(EFFECT_BLOCKS).filter((k) => rec[k] != null).length;
  if (blocks !== 1 || rec[blockKey] == null)
    add(`${base}.${blockKey}`, `${where}: effect '${type}' needs exactly its own '${type}' block`);
  if (inDelay && EFFECT_RUNS[type] === 'server')
    add(`${base}.type`, `${where}: a delay can only run raid effects, not '${type}'`);
  if (inDelay && type === 'delay') add(`${base}.type`, `${where}: delays can't nest`);
  const blk = rec[blockKey] as Record<string, unknown> | null | undefined;
  if (blk == null) return;
  const at = (f: string) => `${base}.${blockKey}.${f}`;
  const s = (f: string) => (blk[f] as string | null | undefined) ?? '';
  const n = (f: string) => num(blk[f] as number | undefined);
  const missions = idSet(b.missions);
  const allies = idSet(b.allies);

  const role = (r: string, allowPlayer: boolean, field = 'role', allowTeam = false) => {
    if (!r) {
      add(at(field), `${where}: '${type}' needs a role`);
      return;
    }
    if (r === 'player') {
      if (!allowPlayer)
        add(at(field), `${where}: '${type}' can never target the player's own survivor`);
      return;
    }
    if (r === 'leader' || (allowTeam && r === 'team')) return;
    if (!roles.has(r))
      add(at(field), `${where}: '${type}' targets role '${r}' the chain doesn't cast`);
  };
  const stacks = (f: string, maxStacks: number, what: string) => {
    const list = (blk[f] as ({ itemId?: string; quantity?: number } | null)[] | undefined) ?? [];
    if (list.length > maxStacks)
      add(at(f), `${where}: '${type}' ${what} has more than ${maxStacks} stacks`);
    list.forEach((x, k) => {
      if (x == null || !x.itemId)
        add(at(`${f}.${k}.itemId`), `${where}: '${type}' has an empty stack`);
      else if (!refs.items.has(x.itemId))
        add(at(`${f}.${k}.itemId`), `${where}: '${type}' names unknown item '${x.itemId}'`);
      if (x != null && (num(x.quantity) < 1 || num(x.quantity) > EFFECT_CAPS.stackQty))
        add(
          at(`${f}.${k}.quantity`),
          `${where}: '${type}' quantity must be 1..${EFFECT_CAPS.stackQty}`,
        );
    });
  };
  const zombie = (id: string) => !!id && refs.zombies.has(id);

  switch (type) {
    case 'give_items':
      if (s('to') !== 'pack' && s('to') !== 'home')
        add(at('to'), `${where}: give_items 'to' must be pack or home`);
      if (((blk.stacks as unknown[] | undefined) ?? []).length === 0)
        add(at('stacks'), `${where}: give_items needs stacks`);
      stacks('stacks', EFFECT_CAPS.giveStacks, 'stacks');
      break;
    case 'take_items': {
      const hasStacks = ((blk.stacks as unknown[] | undefined) ?? []).length > 0;
      if (hasStacks === !!s('tag'))
        add(at('tag'), `${where}: take_items needs stacks or a tag, not both`);
      if (hasStacks) stacks('stacks', EFFECT_CAPS.giveStacks, 'stacks');
      else if (n('qty') < 1 || n('qty') > EFFECT_CAPS.takeQty)
        add(at('qty'), `${where}: take_items qty must be 1..${EFFECT_CAPS.takeQty}`);
      break;
    }
    case 'trade': {
      const give =
        (blk.give as ({ itemId?: string; tag?: string; qty?: number } | null)[] | undefined) ?? [];
      if (give.length < 1 || give.length > EFFECT_CAPS.tradeGive)
        add(at('give'), `${where}: trade gives 1..${EFFECT_CAPS.tradeGive} stacks`);
      give.forEach((g, k) => {
        if (g == null) return;
        const hasItem = !!g.itemId;
        const hasTag = !!g.tag;
        if (hasItem === hasTag)
          add(
            at(`give.${k}`),
            `${where}: a trade's given stack names an itemId or a tag, not both`,
          );
        if (hasItem && !refs.items.has(g.itemId!))
          add(at(`give.${k}.itemId`), `${where}: trade gives unknown item '${g.itemId}'`);
        if (num(g.qty) < 1 || num(g.qty) > EFFECT_CAPS.stackQty)
          add(
            at(`give.${k}.qty`),
            `${where}: a trade's given qty must be 1..${EFFECT_CAPS.stackQty}`,
          );
      });
      const promised =
        (blk.promised as ({ itemId?: string; quantity?: number } | null)[] | undefined) ?? [];
      if (promised.length < 1 || promised.length > EFFECT_CAPS.tradePromised)
        add(at('promised'), `${where}: trade promises 1..${EFFECT_CAPS.tradePromised} stacks`);
      promised.forEach((p, k) => {
        if (p == null) return;
        if (!refs.items.has(p.itemId ?? ''))
          add(
            at(`promised.${k}.itemId`),
            `${where}: trade promises unknown item '${p.itemId ?? ''}'`,
          );
        if (num(p.quantity) < 1 || num(p.quantity) > EFFECT_CAPS.promisedQty)
          add(
            at(`promised.${k}.quantity`),
            `${where}: a trade's promised qty must be 1..${EFFECT_CAPS.promisedQty}`,
          );
      });
      if (!['honest', 'generous', 'short', 'lying'].includes(s('result')))
        add(at('result'), `${where}: trade result must be honest, generous, short or lying`);
      if (s('result') === 'generous' && !refs.tables.has(s('bonusTable')))
        add(at('bonusTable'), `${where}: a generous trade needs an existing bonusTable`);
      if (s('result') === 'lying' && s('lie') !== 'junk' && s('lie') !== 'ambush')
        add(at('lie'), `${where}: a lying trade's lie must be junk or ambush`);
      if (s('junkItemId') && !refs.items.has(s('junkItemId')))
        add(at('junkItemId'), `${where}: trade junkItemId '${s('junkItemId')}' doesn't exist`);
      break;
    }
    case 'reveal_stash':
      if (!s('anchor')) add(at('anchor'), `${where}: reveal_stash needs an anchor`);
      if (!refs.tables.has(s('table')))
        add(at('table'), `${where}: reveal_stash table '${s('table')}' doesn't exist`);
      if (n('rolls') < 1 || n('rolls') > 10)
        add(at('rolls'), `${where}: reveal_stash rolls must be 1..10`);
      break;
    case 'injure':
      role(s('role'), !blk.down);
      if (n('hp') < 1 || n('hp') > EFFECT_CAPS.injureHp)
        add(at('hp'), `${where}: injure hp must be 1..${EFFECT_CAPS.injureHp}`);
      break;
    case 'condition':
      role(s('role'), s('conditionId') !== 'laid_up' && s('conditionId') !== 'bitten');
      if (!['wounded', 'laid_up', 'sick', 'bitten'].includes(s('conditionId')))
        add(at('conditionId'), `${where}: condition must be wounded, laid_up, sick or bitten`);
      if (n('turnHours') < 0 || n('turnHours') > 72)
        add(at('turnHours'), `${where}: condition turnHours must be 0..72`);
      break;
    case 'kill':
      role(s('role'), false);
      break;
    case 'turn':
      role(s('role'), false);
      if (!zombie(s('zombieId')))
        add(at('zombieId'), `${where}: turn zombie '${s('zombieId')}' doesn't exist`);
      break;
    case 'recruit':
      role(s('role'), false);
      if (s('role') === 'leader')
        add(at('role'), `${where}: recruit needs a cast role, not the leader`);
      break;
    case 'spawn':
      if (!zombie(s('zombieId')))
        add(at('zombieId'), `${where}: spawn zombie '${s('zombieId')}' doesn't exist`);
      if (n('count') < 1 || n('count') > EFFECT_CAPS.spawnCount)
        add(at('count'), `${where}: spawn count must be 1..${EFFECT_CAPS.spawnCount}`);
      break;
    case 'wave':
      if (n('size') < 1 && !s('waveId')) add(at('size'), `${where}: wave needs a size or a waveId`);
      if (n('size') > 30) add(at('size'), `${where}: wave size must be at most 30`);
      break;
    case 'noise':
      if (n('radiusM') <= 0 || n('radiusM') > EFFECT_CAPS.noiseRadiusM)
        add(
          at('radiusM'),
          `${where}: noise radiusM must be above 0 and at most ${EFFECT_CAPS.noiseRadiusM}`,
        );
      break;
    case 'objective':
      if (!['add', 'complete', 'fail'].includes(s('op')))
        add(at('op'), `${where}: objective op must be add, complete or fail`);
      if (!s('objectiveId')) add(at('objectiveId'), `${where}: objective needs an objectiveId`);
      break;
    case 'flag':
      if (!s('key')) add(at('key'), `${where}: flag needs a key`);
      if (s('scope') !== 'run' && s('scope') !== 'account')
        add(at('scope'), `${where}: flag scope must be run or account`);
      break;
    case 'mission':
      if (!s('missionId') || !missions.has(s('missionId')))
        add(at('missionId'), `${where}: mission '${s('missionId')}' doesn't exist`);
      break;
    case 'tip':
      if (s('marker') !== 'cache' && s('marker') !== 'stash_site')
        add(at('marker'), `${where}: tip marker must be cache or stash_site`);
      if (s('near') !== 'player' && s('near') !== 'building')
        add(at('near'), `${where}: tip near must be player or building`);
      if (n('maxDistanceM') < 0 || n('maxDistanceM') > 10000)
        add(at('maxDistanceM'), `${where}: tip maxDistanceM must be 0..10000`);
      if (s('missionId') && !missions.has(s('missionId')))
        add(at('missionId'), `${where}: tip mission '${s('missionId')}' doesn't exist`);
      break;
    case 'ally':
      if (!['add', 'cool', 'end'].includes(s('op')))
        add(at('op'), `${where}: ally op must be add, cool or end`);
      if (!s('allyId') || !allies.has(s('allyId')))
        add(at('allyId'), `${where}: ally '${s('allyId')}' isn't an allies[] def`);
      if (s('op') === 'add') role(s('role'), false);
      if (s('disposition') !== 'friendly' && s('disposition') !== 'wary')
        add(at('disposition'), `${where}: ally disposition must be friendly or wary`);
      break;
    case 'boon':
      if (s('target') !== 'team' && !s('target').startsWith('role:'))
        add(at('target'), `${where}: boon target must be team or role:<role>`);
      else if (s('target') !== 'team') role(s('target').slice(5), true, 'target');
      if (n('heal') < 0 || n('heal') > EFFECT_CAPS.boonHeal)
        add(at('heal'), `${where}: boon heal must be 0..${EFFECT_CAPS.boonHeal}`);
      if (s('clear') && s('clear') !== 'wounded')
        add(at('clear'), `${where}: boon clear must be wounded or empty`);
      if (n('heal') === 0 && !s('clear')) add(at('heal'), `${where}: boon needs a heal or a clear`);
      break;
    case 'delay': {
      const then = (blk.then as (EffectDef | null)[] | undefined) ?? [];
      if (!s('id')) add(at('id'), `${where}: delay needs an id`);
      if (n('afterS') < EFFECT_CAPS.delayAfterMin || n('afterS') > EFFECT_CAPS.delayAfterMax)
        add(at('afterS'), `${where}: delay afterS must be 3..120`);
      if (then.length === 0) add(at('then'), `${where}: delay needs then effects`);
      if (s('onRaidEnd') !== 'carry' && s('onRaidEnd') !== 'drop')
        add(at('onRaidEnd'), `${where}: delay onRaidEnd must be carry or drop`);
      if (s('onRaidEnd') === 'carry' && !then.every((t) => t?.type === 'turn'))
        add(
          at('onRaidEnd'),
          `${where}: a delay can only carry over the raid's end with turn effects`,
        );
      if (s('cancelIfDead')) role(s('cancelIfDead'), false, 'cancelIfDead');
      then.forEach((t, k) =>
        validateEffect(t, `${where}.then[${k}]`, at(`then.${k}`), roles, b, refs, true, add),
      );
      break;
    }
    case 'end':
      if (s('as') !== 'extracted' && s('as') !== 'failed')
        add(at('as'), `${where}: end as must be extracted or failed`);
      break;
    case 'train': {
      role(s('role'), true);
      if (!(STAT_IDS as readonly string[]).includes(s('stat')))
        add(
          at('stat'),
          `${where}: train stat '${s('stat')}' isn't a stat (str, end, ref, wit, nrv or cha)`,
        );
      // ADR 0066: the stats section is gone; the server reads the retired default (LegacyStats().Training.BeatMarksMax = 2).
      const maxMarks = 2;
      if (n('marks') < 1 || n('marks') > maxMarks)
        add(
          at('marks'),
          `${where}: train marks must be 1..${maxMarks} (stats.training.beatMarksMax)`,
        );
      break;
    }
    case 'apply_effect': {
      const id = s('id');
      const r = s('role');
      const def = (b.statusEffects ?? []).filter((d) => d != null && d.id === id).at(-1);
      if (def == null) {
        add(at('id'), `${where}: apply_effect id '${id}' isn't a statusEffects[] def`);
        break;
      }
      const kind =
        def.duration === undefined
          ? 'timed'
          : def.duration === null
            ? null
            : (def.duration.kind ?? 'timed');
      if (kind !== 'timed' && kind !== 'until_cleared')
        add(
          at('id'),
          `${where}: apply_effect '${id}' is a zone or derived effect (only timed or until_cleared ones start from a beat)`,
        );
      if (!(def.triggers ?? []).some((t) => t != null && t.event === 'beat'))
        add(at('id'), `${where}: apply_effect '${id}' needs the effect to list the 'beat' trigger`);
      const scope = def.scope ?? 'player';
      if (scope === 'survivor') {
        if (!r)
          add(at('role'), `${where}: apply_effect '${id}' is survivor-scoped and needs a role`);
        else if (r === 'player')
          add(
            at('role'),
            `${where}: apply_effect '${id}' is survivor-scoped, so it can't target the player`,
          );
        else if (r !== 'leader' && !roles.has(r))
          add(at('role'), `${where}: 'apply_effect' targets role '${r}' the chain doesn't cast`);
      } else if (r) {
        add(at('role'), `${where}: apply_effect '${id}' is ${scope}-scoped and takes no role`);
      }
      break;
    }
  }
}

/** shared-cs `StatWeightRules.Validate` (card 19): one table case's `statWeight`. */
function statWeightProblems(
  c: OutcomeTableCase,
  where: string,
  base: string,
  roles: Set<string>,
  add: (p: string, m: string) => void,
) {
  const w = c.statWeight;
  if (w == null) return;
  const stat = w.stat ?? '';
  if (!(STAT_IDS as readonly string[]).includes(stat))
    add(`${base}.stat`, `${where}: statWeight reads unknown stat '${stat}'`);
  const who = w.who === undefined ? 'leader' : (w.who ?? '');
  if (!['leader', 'best', 'player'].includes(who) && !roles.has(who))
    add(
      `${base}.who`,
      `${where}: statWeight who '${who}' must be leader, best, player or a cast role`,
    );
  const pp = w.ppPerPoint ?? 5;
  if (pp < 1 || pp > 10) add(`${base}.ppPerPoint`, `${where}: statWeight ppPerPoint must be 1..10`);
  const maxPp = w.maxPp ?? 20;
  if (maxPp < 1 || maxPp > 20) add(`${base}.maxPp`, `${where}: statWeight maxPp must be 1..20`);
  const picks = new Set<string>();
  for (const p of c.pick ?? []) if (p != null && (p.w ?? 1) > 0) picks.add(p.outcome ?? '');
  const favours = w.favours ?? [];
  if (favours.length === 0) add(`${base}.favours`, `${where}: statWeight needs favours`);
  favours.forEach((f, k) => {
    if (!picks.has(f ?? ''))
      add(
        `${base}.favours.${k}`,
        `${where}: statWeight favours '${f ?? ''}', which the case doesn't pick`,
      );
  });
  if (favours.length > 0 && ![...picks].some((p) => !favours.includes(p)))
    add(`${base}.favours`, `${where}: statWeight favours every pick, so nothing moves`);
}

const RANK: Record<string, number> = { disaster: 0, bad: 1, mixed: 2, good: 3, windfall: 4 };

/**
 * `ContentValidator.BeatWarnings` for one valid chain: a certain tell, a windfall share over `story.tone.maxWindfallShare` (the server
 * simulates 400 tickets; this uses the editor's expected values), and dominant root options.
 */
function beatWarnings(c: BeatChainDef, rules: StoryRulesDef): Sub[] {
  const out: Sub[] = [];
  const where = `Beat chain '${c.id ?? ''}'`;
  (c.tells ?? []).forEach((t, j) => {
    for (const [k, p] of Object.entries(t?.p ?? {}))
      if (p <= 0 || p >= 1)
        out.push([
          `tells.${j}.p`,
          `${where}: tell '${t!.id}' is certain when '${t!.of}' is ${k} (a tell is a clue, never proof).`,
        ]);
  });
  const max = rules.tone?.maxWindfallShare ?? 35;
  const a = analyzeChain(c, { maxDepth: ruleDepth(rules) });
  if (a.toneMix.windfall * 100 > max)
    out.push([
      'outcomes',
      `${where}: ${Math.round(a.toneMix.windfall * 100)}% of its reach is windfall (more than ${max}%).`,
    ]);
  const root = c.beats?.[c.root ?? ''];
  const options = (root?.choice?.options ?? []).filter((o) => o != null);
  if (options.length >= 2) {
    const atLeast = options.map(() => true);
    const strictly = options.map(() => false);
    for (const row of enumerateAssignments(c)) {
      const ranks = options.map((o) => {
        const rs = [...tableDistribution(c.tables?.[o.table ?? ''], row.vars).keys()].map(
          (x) => RANK[c.outcomes?.[x]?.tone ?? ''] ?? -1,
        );
        return rs.length ? { lo: Math.min(...rs), hi: Math.max(...rs) } : { lo: -1, hi: -1 };
      });
      options.forEach((_, i) =>
        options.forEach((__, j) => {
          if (i === j) return;
          if (ranks[i]!.lo < ranks[j]!.hi) atLeast[i] = false;
          if (ranks[i]!.hi > ranks[j]!.lo) strictly[i] = true;
        }),
      );
    }
    options.forEach((o, i) => {
      if (atLeast[i] && strictly[i])
        out.push([
          `beats.${c.root}`,
          `${where}: option '${o.id}' is as good or better than every other option under every roll.`,
        ]);
    });
  }
  return out;
}
